/**
 * Signup Page — uses app CSS vars (bg-background, bg-card, text-foreground)
 * Matches the Login page theme + dark/light transitions
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { ThemeToggle } from '../components/ThemeToggle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, Eye, EyeOff, Loader2, Lock, Mail, User, CheckCircle2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';

const STORAGE_KEY = 'lockene_signup_draft';
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function AmbientDots({ isDark }) {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animFrame;
    const dots = [];
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener('resize', resize);
    for (let i = 0; i < 40; i++) {
      dots.push({
        x: Math.random() * canvas.width, y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.2, vy: (Math.random() - 0.5) * 0.2,
        r: Math.random() * 1.6 + 0.4, alpha: Math.random() * 0.1 + 0.02,
      });
    }
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      dots.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = isDark
          ? `rgba(59, 130, 246, ${p.alpha})`
          : `rgba(37, 99, 235, ${p.alpha * 0.5})`;
        ctx.fill();
      });
      animFrame = requestAnimationFrame(draw);
    };
    animFrame = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(animFrame); window.removeEventListener('resize', resize); };
  }, [isDark]);
  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
}

function PasswordCheck({ met, label }) {
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {met
        ? <CheckCircle2 className="h-3 w-3 text-emerald-500 shrink-0" />
        : <XCircle className="h-3 w-3 text-muted-foreground/40 shrink-0" />}
      <span className={met ? 'text-emerald-500' : 'text-muted-foreground/60'}>{label}</span>
    </div>
  );
}

export default function Signup() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const googleBtnRef = useRef(null);
  const googleCodeClientRef = useRef(null);

  const [formData, setFormData] = useState({ email: '', password: '', confirmPassword: '' });
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordValidation, setPasswordValidation] = useState({ minLength: false, hasUppercase: false, hasLowercase: false, hasNumber: false });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleUnavailable, setGoogleUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Resolve client_id from the BACKEND so FE never drifts from the BE client/secret pair.
    const initGoogle = async () => {
      let clientId = GOOGLE_CLIENT_ID;
      try {
        const res = await authAPI.getGoogleClientId();
        const backendId = res?.data?.client_id || res?.data?.data?.client_id;
        if (backendId) clientId = backendId;
      } catch (e) { /* keep build-time fallback */ }
      if (cancelled) return;
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true; script.defer = true;
      script.onload = () => {
        if (!clientId) { setGoogleUnavailable(true); return; }
        if (window.google?.accounts?.oauth2) {
          // Auth-code flow (popup) — forces `prompt=select_account` so the
          // Google chooser appears rather than the browser's active account.
          try {
            googleCodeClientRef.current = window.google.accounts.oauth2.initCodeClient({
              client_id: clientId,
              scope: 'openid email profile',
              ux_mode: 'popup',
              prompt: 'select_account',
              callback: handleGoogleCodeResponse,
            });
          } catch (e) {
            setGoogleUnavailable(true);
          }
        } else {
          setGoogleUnavailable(true);
        }
      };
      script.onerror = () => setGoogleUnavailable(true);
      document.body.appendChild(script);
    };
    initGoogle();
    return () => { cancelled = true; const s = document.querySelector('script[src="https://accounts.google.com/gsi/client"]'); if (s) s.remove(); };
  }, []);

  const handleGoogleCodeResponse = async (response) => {
    if (!response?.code) {
      if (response?.error && response.error !== 'popup_closed') {
        toast.error('Google sign-in failed', { description: response.error });
      }
      return;
    }
    setGoogleLoading(true);
    try {
      const result = await authAPI.googleCode({ code: response.code });
      if (result.data?.success) {
        localStorage.setItem('access_token', result.data.access_token);
        localStorage.setItem('refresh_token', result.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(result.data.user));
        localStorage.removeItem(STORAGE_KEY);
        toast.success(result.data.is_new_user ? 'Welcome! Account created.' : 'Welcome back!');
        window.location.href = result.data.is_new_user ? '/onboarding/brain-init' : '/dashboard';
      }
    } catch (e) {
      toast.error('Google sign-in failed', { description: e?.response?.data?.detail || 'Please try again.' });
    }
    finally { setGoogleLoading(false); }
  };

  const handleGoogleSignIn = () => {
    if (googleCodeClientRef.current) {
      try {
        googleCodeClientRef.current.requestCode();
      } catch (e) {
        toast.error('Google sign-in failed to open. Please try again.');
      }
    } else {
      toast.error('Google Sign-In is loading. Please try again.');
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) { try { const p = JSON.parse(saved); if (p.email) setFormData(prev => ({ ...prev, email: p.email })); } catch { localStorage.removeItem(STORAGE_KEY); } }
  }, []);

  useEffect(() => {
    if (formData.email) localStorage.setItem(STORAGE_KEY, JSON.stringify({ email: formData.email, timestamp: Date.now() }));
  }, [formData.email]);

  const checkEmailAvailability = useCallback(async (email) => {
    if (!email || !email.includes('@')) { setEmailAvailable(null); return; }
    setEmailChecking(true);
    try {
      const response = await authAPI.checkEmail(email);
      setEmailAvailable(!response.data.exists);
      setEmailError(response.data.exists ? 'This email is already registered' : '');
    } catch { /* silent */ } finally { setEmailChecking(false); }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => { if (formData.email && formData.email.includes('@')) checkEmailAvailability(formData.email); }, 500);
    return () => clearTimeout(timer);
  }, [formData.email, checkEmailAvailability]);

  useEffect(() => {
    const p = formData.password;
    setPasswordValidation({ minLength: p.length >= 8, hasUppercase: /[A-Z]/.test(p), hasLowercase: /[a-z]/.test(p), hasNumber: /[0-9]/.test(p) });
  }, [formData.password]);

  const handleInputChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const isFormValid = () =>
    formData.email && emailAvailable && formData.password &&
    formData.password === formData.confirmPassword &&
    Object.values(passwordValidation).every(v => v);

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;
    setLoading(true);
    try {
      const response = await authAPI.signup({ email: formData.email, password: formData.password, name: formData.email.split('@')[0] });
      if (response.data?.access_token) {
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        localStorage.removeItem(STORAGE_KEY);
        toast.success('Welcome! Account created.');
        window.location.href = '/onboarding/brain-init';
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Signup failed. Please try again.');
    } finally { setLoading(false); }
  };

  const passwordMatch = formData.password && formData.confirmPassword && formData.password === formData.confirmPassword;
  const passwordMismatch = formData.confirmPassword && formData.password !== formData.confirmPassword;

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-4 py-12 relative transition-colors duration-500 ${isDark ? 'bg-background' : 'bg-[hsl(220,14%,97%)]'}`} data-testid="signup-page">
      <AmbientDots isDark={isDark} />

      <style>{`
        @keyframes authFadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes authFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes authLogoIn { from { opacity: 0; transform: scale(0.85); } to { opacity: 1; transform: scale(1); } }
      `}</style>

      {/* Top bar */}
      <div className="fixed top-6 left-8 z-50 animate-[authLogoIn_0.5s_ease-out]">
        <div className={`w-11 h-11 rounded-full overflow-hidden ring-1 ${isDark ? 'ring-white/10' : 'ring-black/10'} shadow-lg`} data-testid="lockene-logo">
          <img src="https://customer-assets.emergentagent.com/job_91eeaace-707a-4157-ada5-289529392665/artifacts/z8mxrkdr_Logosdcvdcvd%20%282%29.png" alt="Lockene AI" className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="fixed top-6 right-8 z-50 animate-[authFadeIn_0.5s_ease-out_0.2s_both]"><ThemeToggle /></div>

      {/* Card */}
      <div className={`w-full max-w-md rounded-2xl border backdrop-blur-xl relative z-10 animate-[authFadeUp_0.5s_ease-out_0.1s_both] ${isDark ? 'bg-card/80 border-border shadow-2xl shadow-black/30' : 'bg-white/90 border-border shadow-xl shadow-gray-200/60'}`} data-testid="signup-card">
        {googleLoading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
            <p className="text-sm text-muted-foreground">Creating your account with Google...</p>
          </div>
        ) : (
          <>
            <div className="pt-10 px-8 pb-2">
              <h1 className="text-2xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="signup-heading">Create Your Account</h1>
              <p className="text-sm mt-1 text-muted-foreground">Get started with your AI-powered CRM</p>
            </div>
            <div className="px-8 pb-10 pt-4">
              <form onSubmit={handleSignup} className="space-y-4">
                <button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={googleUnavailable || googleLoading}
                  data-testid="signup-google"
                  className={`w-full flex items-center justify-center gap-3 h-11 rounded-xl border font-medium text-sm transition
                    ${isDark
                      ? 'bg-white text-gray-900 border-white/10 hover:bg-gray-100'
                      : 'bg-white text-gray-800 border-gray-300 hover:bg-gray-50'}
                    disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true"><path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84c-.21 1.12-.84 2.07-1.79 2.71v2.25h2.9c1.7-1.56 2.69-3.87 2.69-6.6z"/><path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.25c-.8.54-1.83.86-3.06.86-2.35 0-4.35-1.59-5.06-3.72H.96v2.33A9 9 0 0 0 9 18z"/><path fill="#FBBC05" d="M3.94 10.71A5.4 5.4 0 0 1 3.65 9c0-.59.1-1.17.29-1.71V4.96H.96A9 9 0 0 0 0 9c0 1.45.35 2.82.96 4.04l2.98-2.33z"/><path fill="#EA4335" d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l2.98 2.33C4.65 5.17 6.65 3.58 9 3.58z"/></svg>
                  {googleLoading ? 'Signing in…' : 'Sign up with Google'}
                </button>
                {googleUnavailable && (
                  <p className="text-xs text-amber-600 dark:text-amber-400 text-center mt-1" data-testid="signup-google-unavailable">
                    Google sign-up is unavailable on this domain — this site's origin may not be authorized for the Google client. Use email sign-up below.
                  </p>
                )}

                <div className="relative">
                  <div className="h-px bg-border" />
                  <span className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 px-3 text-xs text-muted-foreground ${isDark ? 'bg-card' : 'bg-white'}`}>or sign up with email</span>
                </div>

                {/* Email */}
                <div className="space-y-1.5">
                  <Label htmlFor="email" className={`text-sm font-medium ${emailError ? 'text-destructive' : 'text-foreground/70'}`}>Work Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="email" type="email" placeholder="your@company.com" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} className={`pl-10 pr-10 h-11 rounded-xl ${emailError ? 'border-destructive/50' : emailAvailable ? 'border-emerald-500/50' : ''}`} data-testid="signup-email-input" />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      {emailChecking && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                      {!emailChecking && emailAvailable === true && <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
                      {!emailChecking && emailAvailable === false && <XCircle className="h-4 w-4 text-destructive" />}
                    </div>
                  </div>
                  {emailError && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{emailError}</p>}
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <Label htmlFor="password" className="text-sm font-medium text-foreground/70">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="Create a strong password" value={formData.password} onChange={(e) => handleInputChange('password', e.target.value)} className="pl-10 pr-10 h-11 rounded-xl" data-testid="signup-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/60 transition-colors focus:outline-none" tabIndex={-1}>
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {formData.password && (
                    <div className="grid grid-cols-2 gap-1 mt-1">
                      <PasswordCheck met={passwordValidation.minLength} label="8+ characters" />
                      <PasswordCheck met={passwordValidation.hasUppercase} label="Uppercase" />
                      <PasswordCheck met={passwordValidation.hasLowercase} label="Lowercase" />
                      <PasswordCheck met={passwordValidation.hasNumber} label="Number" />
                    </div>
                  )}
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <Label htmlFor="confirmPassword" className={`text-sm font-medium ${passwordMismatch ? 'text-destructive' : 'text-foreground/70'}`}>Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input id="confirmPassword" type={showConfirm ? 'text' : 'password'} placeholder="Re-enter your password" value={formData.confirmPassword} onChange={(e) => handleInputChange('confirmPassword', e.target.value)} className={`pl-10 pr-10 h-11 rounded-xl ${passwordMismatch ? 'border-destructive/50' : passwordMatch ? 'border-emerald-500/50' : ''}`} data-testid="signup-confirm-password-input" />
                    <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground/60 transition-colors focus:outline-none" tabIndex={-1}>
                      {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  {passwordMismatch && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />Passwords don't match</p>}
                  {passwordMatch && <p className="text-xs text-emerald-500 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" />Passwords match</p>}
                </div>

                <Button type="submit" className="w-full h-11 mt-2 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-semibold shadow-lg shadow-primary/20 transition-all duration-200" disabled={loading || !isFormValid()} data-testid="signup-form-submit-button">
                  {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Creating account...</> : 'Create Account'}
                </Button>

                <p className="text-center text-sm pt-2 text-muted-foreground">
                  Already have an account?{' '}
                  <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">Sign in</Link>
                </p>
              </form>
            </div>
          </>
        )}

        <p className="px-8 pb-6 text-center text-xs text-muted-foreground/70" data-testid="auth-legal-links">
          By creating an account you agree to our{' '}
          <a href="https://lockene.ai/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="terms-link">Terms &amp; Conditions</a>
          {' '}and{' '}
          <a href="https://lockene.ai/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline" data-testid="privacy-link">Privacy Policy</a>.
        </p>
      </div>

      <p className="fixed bottom-4 left-0 right-0 text-center text-xs z-10 animate-[authFadeIn_0.5s_ease-out_0.8s_both] text-muted-foreground/50" data-testid="signup-footer">
        Design and Developed by Lockene Inc
      </p>
    </div>
  );
}
